import { createSelector } from 'reselect';
import get from 'lodash/get';
import { useLocation } from 'react-router-dom';
import { CURRENT_PRODUCT_MODE } from '@utils/constants';
import { humanize } from '@utils';
import { THRESHOLD_BASELINE_MAP, THRESHOLD_INVERTED_METRICS, LESS_THAN_NUMBER } from '@utils/metrics';
import { initThresholdSelector } from '@store/noc/threshold/selectors';

export const THRESHOLD_GOOD = 'Good';
export const THRESHOLD_CRITICAL = 'Critical';
export const THRESHOLD_WARNING = 'Warning';

export const THRESHOLD_WARNING_VALUE = 0.9;

export const THRESHOLD_TYPE_CUSTOM = 'custom';
export const THRESHOLD_TYPE_CONVIVA = 'conviva';

// baseline values are common for both views
export const thresholdBaselineSelector = createSelector(
  initThresholdSelector,
  (res) => res.data.threshold_baseline || {},
);

// calculate threshold for a row
export const calcRowThreshold = ({ rowData, metrics, thresholds = {}, thresholdBaseline = {} }) => {
  const thresholdCategory = {};
  metrics.forEach((metric) => {
    thresholdCategory[metric] = calcThreshold({
      rowData,
      metric,
      threshold: thresholds[rowData.id] || {},
      thresholdBaseline,
    });
  });

  // calculate scope threshold
  const hasCriticalValue = Object.values(thresholdCategory)
    .map((i) => i.category)
    .includes(THRESHOLD_CRITICAL);
  const hasWarningValue = Object.values(thresholdCategory)
    .map((i) => i.isWarning)
    .some(Boolean);

  thresholdCategory[rowData.id] = hasCriticalValue ? THRESHOLD_CRITICAL : THRESHOLD_GOOD;
  thresholdCategory.isCritical = hasCriticalValue ? 1 : 0;
  thresholdCategory.isWarning = hasWarningValue ? 1 : 0;
  // done
  return thresholdCategory;
};

// threshold type
const getThresholdType = (record, metric) =>
  get(record, [metric, 'isCustomSelected'], false) ? THRESHOLD_TYPE_CUSTOM : THRESHOLD_TYPE_CONVIVA;

// threshold state
const getThresholdCategory = (metric, metricValue, metricThreshold) => {
  const critical = THRESHOLD_INVERTED_METRICS.includes(metric)
    ? metricValue < metricThreshold
    : metricValue > metricThreshold;

  return critical ? THRESHOLD_CRITICAL : THRESHOLD_GOOD;
};

const isThresholdWarning = (metricValue, metricThreshold) => {
  if (metricThreshold) {
    return Math.abs(metricValue - metricThreshold) / metricThreshold <= 1 - THRESHOLD_WARNING_VALUE;
  }
  return false;
};

// returns threshold value for a cell
export const calcThreshold = ({ rowData, metric, threshold = {}, thresholdBaseline = {} }) => {
  let category = THRESHOLD_GOOD;
  let isWarning = false;
  let applyThreshold = true;

  const metricValue = rowData[metric];

  // check and apply thresholds
  const thresholdType = getThresholdType(threshold, metric);
  let metricThreshold = get(threshold, [metric, thresholdType], null);

  if (metricThreshold && metricThreshold > 0) {
    try {
      // check for baseline
      const baselineMetric = THRESHOLD_BASELINE_MAP[CURRENT_PRODUCT_MODE][metric] || null;

      if (baselineMetric) {
        // do not apply threshold if baseline is not available
        const baselineThresholdType = getThresholdType(thresholdBaseline, metric);
        const baseline = get(thresholdBaseline, [metric, baselineThresholdType], Infinity);
        // current baseline metric value
        const rowBaseline = rowData[baselineMetric];
        // apply threshold only when baseline is crossed
        if (rowBaseline < baseline) {
          applyThreshold = false;
        }
      }
    } catch (err) {
      // do not apply threshold in case of calc errors
      applyThreshold = false;
    }

    if (metric === 'Bitrate') {
      metricThreshold = metricThreshold * 1024;
    }

    // find treshold category
    if (applyThreshold) {
      category = getThresholdCategory(metric, metricValue, metricThreshold);
      isWarning = isThresholdWarning(metricValue, metricThreshold);
    }
  }
  return {
    category,
    metricThreshold,
    isWarning,
  };
};

// wrapper around humanize to handle display requirments
export const humanizeValue = (number: number, unit: string) => {
  // for very small numbers
  if (number && number < LESS_THAN_NUMBER) {
    return { humanizedNumber: '< 0.001', humanizedUnit: unit };
  }

  // humanize
  const humanizedValue = humanize(number, unit);
  let data = {
    humanizedNumber: humanizedValue,
    humanizedUnit: unit,
  };

  // split prefix unit if any
  try {
    if (isNaN(humanizedValue)) {
      // eslint-disable-next-line no-useless-escape
      const splitValue = humanizedValue.toString().match(/([\d\.]+)|(\D+)/g);
      if (splitValue) {
        data = {
          humanizedNumber: splitValue[0] || humanizedValue,
          humanizedUnit: `${splitValue[1] || ''}${unit}`,
        };
      }
    }
  } catch (err) {
    // defaults to original humanized value
  }

  return data;
};

export const autoSort = (isAutoSortEnabled, data) => {
  // moving red tiles to the top
  if (isAutoSortEnabled) {
    data.sort((item1, item2) => {
      if (item1.threshold.isCritical === item2.threshold.isCritical) {
        return item2.threshold.isWarning - item1.threshold.isWarning;
      }
      return item2.threshold.isCritical - item1.threshold.isCritical;
    });
  }
};

export const CheckIfUpdateDashboardPopupIsOpen = (updatePopupObj = {}) => {
  const location = useLocation();
  const currentLocation = location.pathname;
  const params = currentLocation.split('/');
  const activeDashboardId = params[params.length - 1];
  return updatePopupObj[activeDashboardId] && updatePopupObj[activeDashboardId] === true;
};

export enum Category {
  Good = 'Good',
  Warning = 'Warning',
  Critical = 'Critical',
}
